import React from 'react';
import { db } from '../data/store';
import { syncedGet, syncedSet } from '../data/serverStore';
import { getDefaultUnit, getParamMeta } from '../data/params';

const todayStr = () => new Date().toISOString().split('T')[0];
const FIELDS = [
  { key:'dilutionFactor', label:'DF', aliases:['df','dilutionfactor','dilution'] },
  { key:'reportingLimit', label:'RL', aliases:['rl','rdl','reportinglimit','reportablelimit'] },
  { key:'value', label:'Result', aliases:['result','results','value'] },
  { key:'verdict', label:'Verdict', aliases:['verdict','qualifier'] },
  { key:'unit', label:'Unit', aliases:['unit','units'] },
  { key:'method', label:'Analytical Method', aliases:['method','analyticalmethod','testmethod'] },
  { key:'spec', label:'Acceptance Limits', aliases:['spec','specification','specificationacceptancelimits','acceptancelimit','acceptancelimits'] },
  { key:'dateTested', label:'Date Tested', aliases:['date','datetested','dateanalyzed','analysisdate'] },
];
const BULK_FIELDS = FIELDS.filter(field => !['value','verdict'].includes(field.key));
const VERDICT_OPTIONS = [
  { value:'', label:'(auto)' },
  { value:'pass', label:'Pass' },
  { value:'fail', label:'Fail' },
  { value:'less', label:'Less Than' },
  { value:'more', label:'Greater Than' },
];

const loadMap = key => {
  const value = syncedGet(key, {});
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
};
const normalize = value => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
const isSampleIdHeader = value => [
  'sampleid', 'sample', 'sampleno', 'samplenumber', 'samplename', 'id',
].includes(normalize(value));
const fieldForHeader = value => {
  const key = normalize(value);
  return FIELDS.find(field => field.aliases.includes(key))?.key || null;
};
const parseClipboard = text => String(text || '')
  .replace(/\r/g, '')
  .split('\n')
  .filter((row, index, rows) => row !== '' || index < rows.length - 1)
  .map(row => row.split('\t'));

const canonicalize = value => {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') return Object.keys(value).sort().reduce((result, key) => {
    result[key] = canonicalize(value[key]);
    return result;
  }, {});
  return value;
};
const sameData = (left, right) => JSON.stringify(canonicalize(left)) === JSON.stringify(canonicalize(right));
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const defaultDraft = (parameter, existing={}, sample={}) => {
  const matrix = sample.solidLiq === 'S' ? 'S' : sample.solidLiq === 'T' ? 'T' : 'L';
  const resultDefaults = loadMap(`sl_result_field_defaults_${matrix}`)[parameter] || {};
  const parameterMeta = loadMap('sl_param_meta')[parameter] || getParamMeta(parameter) || {};
  const unitPrefs = loadMap(`sl_unit_prefs_${matrix}`);
  const methodPrefs = loadMap(`sl_method_prefs_${matrix}`);
  const baseUnit = getDefaultUnit(parameter);
  const matrixUnit = matrix === 'S'
    ? (parameterMeta.unitSolid || (baseUnit === 'mg/L' ? 'mg/kg' : baseUnit === 'ug/L' ? 'ug/kg' : baseUnit))
    : matrix === 'T' ? (parameterMeta.unitTclp || 'mg/L')
      : (parameterMeta.unitLiquid || parameterMeta.unit || baseUnit);
  const matrixMethod = matrix === 'S'
    ? (parameterMeta.methodSolid || parameterMeta.method || '')
    : matrix === 'T' ? (parameterMeta.methodTclp || '')
      : (parameterMeta.methodLiquid || parameterMeta.method || '');
  const matrixSpec = matrix === 'S'
    ? (parameterMeta.specSolid ?? parameterMeta.spec ?? '')
    : matrix === 'T' ? (parameterMeta.specTclp ?? '')
      : (parameterMeta.specLiquid ?? parameterMeta.spec ?? '');
  return {
    value: existing.value ?? '',
    verdict: existing.verdict || '',
    reportingLimit: existing.reportingLimit !== undefined
      ? existing.reportingLimit
      : (existing.rdl !== undefined ? existing.rdl : (resultDefaults.reportingLimit ?? parameterMeta.reportingLimit ?? '')),
    dilutionFactor: existing.dilutionFactor !== undefined ? existing.dilutionFactor : '',
    unit: existing.unit !== undefined
      ? existing.unit
      : (hasOwn(resultDefaults, 'unit')
        ? resultDefaults.unit
        : (hasOwn(unitPrefs, parameter) ? unitPrefs[parameter] : matrixUnit)),
    method: existing.method !== undefined
      ? existing.method
      : (resultDefaults.method ?? methodPrefs[parameter] ?? matrixMethod),
    spec: existing.spec !== undefined
      ? existing.spec
      : (hasOwn(resultDefaults, 'spec') ? resultDefaults.spec : matrixSpec),
    dateTested: existing.dateTested || todayStr(),
  };
};

export default function WorkOrderResultsGrid({ activeSample, session, toast, onClose, onSaved }) {
  const workOrder = String(activeSample?.workOrder || '');
  const samples = React.useMemo(() => db.getSamples()
    .filter(sample => String(sample.workOrder || '') === workOrder)
    .sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric:true, sensitivity:'base' })), [workOrder]);

  const parameters = React.useMemo(() => {
    const ordered = [];
    const seen = new Set();
    samples.forEach(sample => (sample.params || []).forEach(parameter => {
      if (!seen.has(parameter)) { seen.add(parameter); ordered.push(parameter); }
    }));
    return ordered.sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity:'base' }));
  }, [samples]);

  // The active sample supplies the starting order users already see in Enter
  // Results. Parameters unique to other samples are appended without changing
  // their assignment or result data.
  const initialParameterOrder = React.useMemo(() => {
    const ordered = [];
    const seen = new Set();
    const add = parameter => {
      if (!parameter || seen.has(parameter)) return;
      seen.add(parameter);
      ordered.push(parameter);
    };
    (activeSample?.params || []).forEach(add);
    samples.forEach(sample => (sample.params || []).forEach(add));
    return ordered;
  }, [activeSample, samples]);

  const initialParameter = (activeSample?.params || []).find(parameter => parameters.includes(parameter)) || parameters[0] || '';
  const [gridMode, setGridMode] = React.useState('parameter');
  const [selectedParameter, setSelectedParameter] = React.useState(initialParameter);
  const [selectedSampleId, setSelectedSampleId] = React.useState(String(activeSample?.id || samples[0]?.id || ''));
  const [selectedRows, setSelectedRows] = React.useState(new Set());
  const [showOrderPanel, setShowOrderPanel] = React.useState(false);
  const [parameterOrder, setParameterOrder] = React.useState(initialParameterOrder);
  const [orderSelection, setOrderSelection] = React.useState(initialParameterOrder[0] || '');
  const [orderChanged, setOrderChanged] = React.useState(false);
  const [includeSpecificationForWorkOrder, setIncludeSpecificationForWorkOrder] = React.useState(
    samples.length > 0 && samples.every(sample => sample.includeSpecificationInReport === true)
  );
  const [roundDigits, setRoundDigits] = React.useState('2');
  const [drafts, setDrafts] = React.useState(() => Object.fromEntries(samples.map(sample => [
    String(sample.id),
    Object.fromEntries((sample.params || []).map(parameter => [parameter, defaultDraft(parameter, sample.results?.[parameter], sample)])),
  ])));
  const [changedCells, setChangedCells] = React.useState(new Set());
  const [bulkValues, setBulkValues] = React.useState({ reportingLimit:'', dilutionFactor:'', unit:'', method:'', spec:'', dateTested:todayStr() });
  const [saving, setSaving] = React.useState(false);
  const [progress, setProgress] = React.useState(null);
  const [pasteMessage, setPasteMessage] = React.useState('');
  // Bulk Excel paste is an import-only mapping; it never writes to sample.params.
  const [showBulkPaste, setShowBulkPaste] = React.useState(false);
  const [bulkPasteText, setBulkPasteText] = React.useState('');
  const [bulkPasteMappings, setBulkPasteMappings] = React.useState([]);
  const [bulkPasteStart, setBulkPasteStart] = React.useState('0');
  const bulkPasteRows = React.useMemo(() => parseClipboard(bulkPasteText).filter(row => row.some(v => v.trim() !== '')), [bulkPasteText]);
  const bulkPasteWidth = bulkPasteRows.reduce((max, row) => Math.max(max, row.length), 0);
  const bulkPasteMap = Array.from({ length:bulkPasteWidth }, (_, i) => bulkPasteMappings[i] || '');
  const bulkPasteStartIndex = Number(bulkPasteStart);
  const bulkPasteErrors = [];
  if (bulkPasteRows.length && bulkPasteRows.some(row => row.length !== bulkPasteWidth)) bulkPasteErrors.push('Inconsistent Excel column counts.');
  if (bulkPasteRows.length > samples.length - bulkPasteStartIndex) bulkPasteErrors.push('More pasted rows than available samples from the selected starting row.');
  if (bulkPasteRows.length && bulkPasteMap.some(value => !value)) bulkPasteErrors.push('Map every pasted column to a parameter.');
  if (new Set(bulkPasteMap.filter(Boolean)).size !== bulkPasteMap.filter(Boolean).length) bulkPasteErrors.push('Each parameter can be selected only once.');
  const bulkPasteUpdates = [];
  let bulkPasteSkipped = 0;
  let bulkPasteReplaced = 0;
  bulkPasteRows.forEach((row, rowIndex) => {
    const sample = samples[bulkPasteStartIndex + rowIndex];
    if (!sample) return;
    row.forEach((raw, column) => {
      const parameter = bulkPasteMap[column];
      if (!parameter || raw.trim() === '') { bulkPasteSkipped += 1; return; }
      if (!(sample.params || []).includes(parameter)) {
        bulkPasteErrors.push(`Sample ${sample.id} does not have parameter ${parameter}.`);
        return;
      }
      const existing = drafts[String(sample.id)]?.[parameter]?.value;
      if (existing !== undefined && existing !== null && String(existing).trim() !== '' && String(existing) !== raw) bulkPasteReplaced += 1;
      bulkPasteUpdates.push({ sampleId:sample.id, parameter, raw });
    });
  });
  const applyMappedPaste = () => {
    if (!bulkPasteRows.length || bulkPasteErrors.length || saving) return;
    if (bulkPasteReplaced && !window.confirm(`This paste will replace ${bulkPasteReplaced} existing/draft result(s). Continue?`)) return;
    const updates = bulkPasteUpdates.flatMap(({ sampleId, parameter, raw }) => {
      const rl = drafts[String(sampleId)]?.[parameter]?.reportingLimit ?? '';
      return normalizeResultUpdatesForParameter(sampleId, parameter, raw, rl);
    });
    applyUpdates(updates);
    setPasteMessage(`Mapped ${bulkPasteUpdates.length} result(s) across ${bulkPasteRows.length} sample(s). ${bulkPasteSkipped} blank/unmapped cells skipped. Review then click Save All Results.`);
    setShowBulkPaste(false);
  };

  const assignedSamples = React.useMemo(() => samples.filter(sample =>
    (sample.params || []).includes(selectedParameter)
  ), [samples, selectedParameter]);
  const selectedSample = samples.find(sample => String(sample.id) === selectedSampleId) || samples[0] || null;
  const selectedSampleParameters = selectedSample?.params || [];
  const cellKey = (sampleId, parameter, field) => `${String(sampleId)}\u0000${parameter}\u0000${field}`;

  React.useEffect(() => {
    const first = assignedSamples[0];
    const draft = first ? drafts[String(first.id)]?.[selectedParameter] : null;
    setBulkValues({
      reportingLimit: draft?.reportingLimit ?? '',
      dilutionFactor: draft?.dilutionFactor ?? '',
      unit: draft?.unit ?? '',
      method: draft?.method ?? '',
      spec: draft?.spec ?? '',
      dateTested: draft?.dateTested || todayStr(),
    });
    setPasteMessage('');
  }, [selectedParameter]); // Deliberately do not reset bulk controls on every cell edit.

  const applyUpdates = updates => {
    if (!updates.length) return;
    setDrafts(previous => {
      const next = { ...previous };
      updates.forEach(({ sampleId, parameter, field, value }) => {
        const id = String(sampleId);
        next[id] = { ...(next[id] || {}) };
        const sample = samples.find(item => String(item.id) === id) || {};
        next[id][parameter] = { ...(next[id][parameter] || defaultDraft(parameter, {}, sample)), [field]:value };
      });
      return next;
    });
    setChangedCells(previous => {
      const next = new Set(previous);
      updates.forEach(({ sampleId, parameter, field }) => next.add(cellKey(sampleId, parameter, field)));
      return next;
    });
  };

  const changeCell = (sampleId, field, value) =>
    applyUpdates([{ sampleId, parameter:selectedParameter, field, value }]);

  const changeSampleParameterCell = (parameter, field, value) =>
    applyUpdates([{ sampleId:selectedSampleId, parameter, field, value }]);

  const normalizeResultUpdatesForParameter = (sampleId, parameter, rawValue, reportingLimit) => {
    const id = String(sampleId);
    const resultNumber = Number(String(rawValue ?? '').trim());
    const rlText = String(reportingLimit ?? '').trim();
    const rlNumber = Number(rlText);
    const currentVerdict = drafts[id]?.[parameter]?.verdict || '';
    if (rawValue !== '' && rlText !== '' && Number.isFinite(resultNumber) && Number.isFinite(rlNumber) && resultNumber < rlNumber) {
      return [
        { sampleId:id, parameter, field:'value', value:rlText },
        { sampleId:id, parameter, field:'verdict', value:'less' },
      ];
    }
    return [
      { sampleId:id, parameter, field:'value', value:rawValue },
      ...(currentVerdict === 'less' && rawValue !== '' && rlText !== '' &&
          Number.isFinite(resultNumber) && Number.isFinite(rlNumber) && resultNumber > rlNumber
        ? [{ sampleId:id, parameter, field:'verdict', value:'' }]
        : []),
    ];
  };

  const normalizeResultUpdates = (sampleId, rawValue, reportingLimit) =>
    normalizeResultUpdatesForParameter(sampleId, selectedParameter, rawValue, reportingLimit);

  const normalizeResultCell = (sampleId, reportingLimitOverride) => {
    const draft = drafts[String(sampleId)]?.[selectedParameter] || {};
    const reportingLimit = reportingLimitOverride !== undefined
      ? reportingLimitOverride
      : draft.reportingLimit;
    applyUpdates(normalizeResultUpdates(sampleId, draft.value ?? '', reportingLimit ?? ''));
  };

  const normalizePastedUpdates = updates => {
    const grouped = new Map();
    updates.forEach(update => {
      const id = String(update.sampleId);
      const key = `${id}\u0000${update.parameter}`;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(update);
    });
    const normalized = [];
    grouped.forEach(sampleUpdates => {
      const sampleId = String(sampleUpdates[0]?.sampleId || '');
      const parameter = sampleUpdates[0]?.parameter || '';
      const resultUpdate = sampleUpdates.find(update => update.field === 'value');
      normalized.push(...sampleUpdates.filter(update => update.field !== 'value'));
      if (!resultUpdate) return;
      const pastedRl = sampleUpdates.find(update => update.field === 'reportingLimit')?.value;
      const reportingLimit = pastedRl !== undefined
        ? pastedRl
        : drafts[sampleId]?.[parameter]?.reportingLimit;
      normalized.push(...normalizeResultUpdatesForParameter(sampleId, parameter, resultUpdate.value, reportingLimit));
    });
    return normalized;
  };

  const normalizeSingleSampleResultCell = (parameter, reportingLimitOverride) => {
    const draft = drafts[selectedSampleId]?.[parameter] || {};
    const reportingLimit = reportingLimitOverride !== undefined
      ? reportingLimitOverride
      : draft.reportingLimit;
    applyUpdates(normalizeResultUpdatesForParameter(
      selectedSampleId,
      parameter,
      draft.value ?? '',
      reportingLimit ?? ''
    ));
  };

  const applyBulk = (field, selectedOnly=false) => {
    if (!selectedParameter || !assignedSamples.length) return;
    const targets = selectedOnly
      ? assignedSamples.filter(sample => selectedRows.has(String(sample.id)))
      : assignedSamples;
    if (!targets.length) {
      setPasteMessage('Select at least one assigned sample row first.');
      return;
    }
    const updates = targets.map(sample => ({
      sampleId:sample.id, parameter:selectedParameter, field, value:bulkValues[field],
    }));
    if (field === 'reportingLimit') {
      targets.forEach(sample => {
        const sampleId = String(sample.id);
        const currentValue = drafts[sampleId]?.[selectedParameter]?.value;
        if (currentValue === '' || currentValue === null || currentValue === undefined) return;
        updates.push(...normalizeResultUpdates(sampleId, currentValue, bulkValues[field]));
      });
    }
    applyUpdates(updates);
    setPasteMessage(`${FIELDS.find(item => item.key === field)?.label || field} applied to ${targets.length} sample(s).`);
  };

  const toggleRow = sampleId => setSelectedRows(previous => {
    const next = new Set(previous);
    const id = String(sampleId);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAllAssigned = () => setSelectedRows(previous => {
    const assignedIds = assignedSamples.map(sample => String(sample.id));
    const allSelected = assignedIds.length > 0 && assignedIds.every(id => previous.has(id));
    const next = new Set(previous);
    assignedIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
    return next;
  });

  const roundSelectedResults = () => {
    const digits = Number(roundDigits);
    const targets = assignedSamples.filter(sample => selectedRows.has(String(sample.id)));
    if (!targets.length) {
      setPasteMessage('Select the sample rows whose Result values should be rounded.');
      return;
    }
    const updates = [];
    let skipped = 0;
    targets.forEach(sample => {
      const sampleId = String(sample.id);
      const current = drafts[sampleId]?.[selectedParameter]?.value;
      if (current === '' || current === null || current === undefined || !Number.isFinite(Number(current))) {
        skipped += 1;
        return;
      }
      updates.push({ sampleId, parameter:selectedParameter, field:'value', value:Number(current).toFixed(digits) });
    });
    applyUpdates(updates);
    setPasteMessage(`Rounded ${updates.length} selected result(s) to ${digits} decimal place${digits === 1 ? '' : 's'}${skipped ? `; ${skipped} blank/non-numeric value(s) skipped` : ''}.`);
  };

  const applyLessThanRule = (selectedOnly=false) => {
    const targets = selectedOnly
      ? assignedSamples.filter(sample => selectedRows.has(String(sample.id)))
      : assignedSamples;
    if (!targets.length) {
      setPasteMessage(selectedOnly
        ? 'Select at least one assigned sample row first.'
        : 'No samples are assigned the selected parameter.');
      return;
    }
    const updates = [];
    let adjusted = 0;
    let cleared = 0;
    let skipped = 0;
    targets.forEach(sample => {
      const sampleId = String(sample.id);
      const draft = drafts[sampleId]?.[selectedParameter] || {};
      const resultText = String(draft.value ?? '').trim();
      const rlText = String(draft.reportingLimit ?? '').trim();
      const resultNumber = Number(resultText);
      const rlNumber = Number(rlText);
      if (resultText === '' || rlText === '' || !Number.isFinite(resultNumber) || !Number.isFinite(rlNumber)) {
        skipped += 1;
        return;
      }
      if (resultNumber <= rlNumber) {
        updates.push(
          { sampleId, parameter:selectedParameter, field:'value', value:rlText },
          { sampleId, parameter:selectedParameter, field:'verdict', value:'less' },
        );
        adjusted += 1;
      } else if (draft.verdict === 'less') {
        updates.push({ sampleId, parameter:selectedParameter, field:'verdict', value:'' });
        cleared += 1;
      }
    });
    applyUpdates(updates);
    setPasteMessage(`< RL rule checked ${targets.length} row(s): ${adjusted} adjusted${cleared ? `, ${cleared} outdated Less Than verdict(s) cleared` : ''}${skipped ? `, ${skipped} blank/non-numeric row(s) skipped` : ''}.`);
  };

  const pasteAt = (event, startSampleIndex, startFieldIndex) => {
    const text = event.clipboardData?.getData('text/plain');
    if (!text || !selectedParameter) return;
    const matrix = parseClipboard(text);
    if (!matrix.length) return;
    event.preventDefault();

    const updates = [];
    const header = matrix[0] || [];
    const sampleIdColumn = header.findIndex(isSampleIdHeader);
    const mappedFields = header.map(fieldForHeader);
    const validSamples = new Map(samples.map(sample => [String(sample.id), sample]));

    if (sampleIdColumn >= 0 && mappedFields.some(Boolean)) {
      matrix.slice(1).forEach(row => {
        const sampleId = String(row[sampleIdColumn] ?? '').trim();
        const sample = validSamples.get(sampleId);
        if (!sample || !(sample.params || []).includes(selectedParameter)) return;
        mappedFields.forEach((field, columnIndex) => {
          if (!field || columnIndex === sampleIdColumn || row[columnIndex] === undefined) return;
          updates.push({ sampleId, parameter:selectedParameter, field, value:row[columnIndex] });
        });
      });
      const normalizedUpdates = normalizePastedUpdates(updates);
      applyUpdates(normalizedUpdates);
      setPasteMessage(normalizedUpdates.length
        ? `Matched ${normalizedUpdates.length} cell(s) by Sample ID and column header. Results below RL were reported as Less Than the RL value.`
        : 'No matching assigned Sample IDs were found in the pasted data.');
      return;
    }

    matrix.forEach((row, rowOffset) => {
      const sample = samples[startSampleIndex + rowOffset];
      if (!sample || !(sample.params || []).includes(selectedParameter)) return;
      row.forEach((value, columnOffset) => {
        const field = FIELDS[startFieldIndex + columnOffset]?.key;
        if (field) updates.push({ sampleId:sample.id, parameter:selectedParameter, field, value });
      });
    });
    const normalizedUpdates = normalizePastedUpdates(updates);
    applyUpdates(normalizedUpdates);
    setPasteMessage(`Pasted ${normalizedUpdates.length} cell(s) for ${selectedParameter}. Results below RL were reported as Less Than the RL value.`);
  };

  const pasteSingleSampleAt = (event, startParameterIndex, startFieldIndex) => {
    const text = event.clipboardData?.getData('text/plain');
    if (!text || !selectedSample) return;
    const matrix = parseClipboard(text);
    if (!matrix.length) return;
    event.preventDefault();
    const updates = [];
    matrix.forEach((row, rowOffset) => {
      const parameter = selectedSampleParameters[startParameterIndex + rowOffset];
      if (!parameter) return;
      row.forEach((value, columnOffset) => {
        const field = FIELDS[startFieldIndex + columnOffset]?.key;
        if (field) updates.push({
          sampleId:selectedSample.id,
          parameter,
          field,
          value,
        });
      });
    });
    const normalizedUpdates = normalizePastedUpdates(updates);
    applyUpdates(normalizedUpdates);
    setPasteMessage(`Pasted ${normalizedUpdates.length} cell(s) into Sample ${selectedSample.id} across ${new Set(normalizedUpdates.map(update => update.parameter)).size} parameter row(s). Results below RL were reported as Less Than the RL value.`);
  };

  const changedSampleIds = [...new Set([...changedCells].map(key => key.split('\u0000')[0]))];

  const saveChangedUnitAndSpecDefaults = async () => {
    const defaultsByMatrix = new Map();
    samples.forEach(sample => {
      const sampleId = String(sample.id);
      const matrix = sample.solidLiq === 'S' ? 'S' : sample.solidLiq === 'T' ? 'T' : 'L';
      (sample.params || []).forEach(parameter => {
        const fields = ['unit', 'spec'].filter(field =>
          changedCells.has(cellKey(sampleId, parameter, field))
        );
        if (!fields.length) return;
        if (!defaultsByMatrix.has(matrix)) {
          defaultsByMatrix.set(matrix, loadMap(`sl_result_field_defaults_${matrix}`));
        }
        const defaults = defaultsByMatrix.get(matrix);
        const next = { ...(defaults[parameter] || {}) };
        fields.forEach(field => { next[field] = drafts[sampleId]?.[parameter]?.[field] ?? ''; });
        defaults[parameter] = next;
      });
    });
    for (const [matrix, defaults] of defaultsByMatrix.entries()) {
      await syncedSet(`sl_result_field_defaults_${matrix}`, defaults);
    }
  };

  const moveOrderSelection = destination => {
    const currentIndex = parameterOrder.indexOf(orderSelection);
    if (currentIndex < 0 || !parameterOrder.length) return;
    const nextIndex = destination === 'top' ? 0
      : destination === 'bottom' ? parameterOrder.length - 1
        : Math.max(0, Math.min(parameterOrder.length - 1, currentIndex + destination));
    if (nextIndex === currentIndex) return;
    setParameterOrder(previous => {
      const next = [...previous];
      const [moved] = next.splice(currentIndex, 1);
      next.splice(nextIndex, 0, moved);
      return next;
    });
    setOrderChanged(true);
  };

  const orderedParamsForSample = sample => {
    const original = Array.isArray(sample.params) ? sample.params : [];
    const assigned = new Set(original);
    const ordered = parameterOrder.filter(parameter => assigned.has(parameter));
    original.forEach(parameter => {
      if (!ordered.includes(parameter)) ordered.push(parameter);
    });
    return ordered;
  };

  const saveParameterOrder = async () => {
    if (!orderChanged || saving) return;
    if (changedCells.size) {
      setPasteMessage('Save or cancel changed result cells before applying the parameter order.');
      return;
    }
    const targets = samples
      .map(sample => ({ sample, params:orderedParamsForSample(sample) }))
      .filter(({ sample, params }) => !sameData(sample.params || [], params));
    if (!targets.length) {
      setOrderChanged(false);
      setPasteMessage('All samples already use this parameter order.');
      return;
    }
    if (!window.confirm(
      `Apply this parameter order to ${targets.length} sample(s) in Work Order ${workOrder}?\n\nOnly the params order will be updated. Stored results will not be sent or changed.`
    )) return;

    const completed = [];
    setSaving(true);
    setProgress({ done:0, total:targets.length });
    try {
      for (let index = 0; index < targets.length; index += 1) {
        const { sample, params } = targets[index];
        const saved = await db.updateSample(sample.id, { params });
        const record = saved?.record;
        if (!record) throw new Error(`Sample ${sample.id}: server did not return the saved record`);
        // Once the server returned a record, include this sample in any
        // rollback attempt before performing post-save verification.
        completed.push(sample);
        if (!sameData(record.params || [], params)) {
          throw new Error(`Sample ${sample.id}: server verification failed for parameter order`);
        }
        if (!sameData(record.results || {}, sample.results || {})) {
          throw new Error(`Sample ${sample.id}: result verification failed; order update stopped`);
        }
        setProgress({ done:index + 1, total:targets.length });
      }
    } catch (error) {
      let rollbackFailures = 0;
      for (let index = completed.length - 1; index >= 0; index -= 1) {
        try {
          await db.updateSample(completed[index].id, { params:[...(completed[index].params || [])] });
        } catch {
          rollbackFailures += 1;
        }
      }
      setSaving(false);
      setProgress(null);
      toast('error', `${error.message}. ${completed.length - rollbackFailures} earlier order update(s) restored${rollbackFailures ? `; ${rollbackFailures} rollback(s) require review` : ''}. Results were never included in the update payload.`);
      return;
    }

    setSaving(false);
    setProgress(null);
    setOrderChanged(false);
    toast('success', `Parameter order saved for ${targets.length} sample(s) in Work Order ${workOrder}. Results were unchanged.`);
    onSaved && onSaved();
    onClose();
  };

  const saveWorkOrderSpecificationSetting = async () => {
    if (saving) return;
    if (changedCells.size) {
      setPasteMessage('Save or cancel changed result cells before applying the report Specification setting.');
      return;
    }
    const desired = includeSpecificationForWorkOrder === true;
    const targets = samples.filter(sample =>
      (sample.includeSpecificationInReport === true) !== desired
    );
    if (!targets.length) {
      setPasteMessage(`Specification column is already ${desired ? 'included for' : 'excluded from'} every sample in this work order.`);
      return;
    }
    if (!window.confirm(
      `${desired ? 'Include' : 'Exclude'} the Specification column for all ${samples.length} samples in Work Order ${workOrder}?\n\nOnly the report-column setting will be updated. Stored results and specifications will not be sent or changed.`
    )) return;

    const completed = [];
    setSaving(true);
    setProgress({ done:0, total:targets.length });
    try {
      for (let index = 0; index < targets.length; index += 1) {
        const sample = targets[index];
        const saved = await db.updateSample(sample.id, { includeSpecificationInReport:desired });
        const record = saved?.record;
        if (!record) throw new Error(`Sample ${sample.id}: server did not return the saved record`);
        completed.push(sample);
        if ((record.includeSpecificationInReport === true) !== desired) {
          throw new Error(`Sample ${sample.id}: report Specification setting verification failed`);
        }
        if (!sameData(record.results || {}, sample.results || {})) {
          throw new Error(`Sample ${sample.id}: result verification failed; update stopped`);
        }
        setProgress({ done:index + 1, total:targets.length });
      }
    } catch (error) {
      let rollbackFailures = 0;
      for (let index = completed.length - 1; index >= 0; index -= 1) {
        try {
          await db.updateSample(completed[index].id, {
            includeSpecificationInReport:completed[index].includeSpecificationInReport === true,
          });
        } catch {
          rollbackFailures += 1;
        }
      }
      setSaving(false);
      setProgress(null);
      toast('error', `${error.message}. ${completed.length - rollbackFailures} earlier setting update(s) restored${rollbackFailures ? `; ${rollbackFailures} rollback(s) require review` : ''}. Results were never included in the update payload.`);
      return;
    }

    setSaving(false);
    setProgress(null);
    toast('success', `Specification column ${desired ? 'included for' : 'excluded from'} all ${samples.length} samples in Work Order ${workOrder}. Results were unchanged.`);
    onSaved && onSaved();
    onClose();
  };

  const saveAll = async (markWorkOrderComplete=false) => {
    if ((!changedCells.size && !markWorkOrderComplete) || saving) return;
    if (markWorkOrderComplete && !window.confirm(
      `Save all changed results and mark all ${samples.length} samples in Work Order ${workOrder} complete?\n\nCompleted samples will appear in the Completed Reports view.`
    )) return;

    const targets = markWorkOrderComplete
      ? samples.filter(sample => changedSampleIds.includes(String(sample.id)) || !sample.completed)
      : samples.filter(sample => changedSampleIds.includes(String(sample.id)));
    const failures = [];
    setSaving(true);
    setProgress({ done:0, total:targets.length });

    for (let index = 0; index < targets.length; index += 1) {
      const sample = targets[index];
      const sampleId = String(sample.id);
      const sampleChanged = changedSampleIds.includes(sampleId);
      const results = { ...(sample.results || {}) };
      if (sampleChanged) (sample.params || []).forEach(parameter => {
        const changedFields = FIELDS.filter(field => changedCells.has(cellKey(sampleId, parameter, field.key)));
        if (!changedFields.length) return;
        const original = results[parameter] || {};
        const draft = drafts[sampleId]?.[parameter] || defaultDraft(parameter, original, sample);
        const patch = Object.fromEntries(changedFields.map(field => [field.key, draft[field.key]]));
        const merged = { ...original, ...defaultDraft(parameter, original, sample), ...patch };
        const resultText = String(merged.value ?? '').trim();
        const rlText = String(merged.reportingLimit ?? merged.rdl ?? '').trim();
        const resultNumber = Number(resultText);
        const rlNumber = Number(rlText);
        if (resultText !== '' && rlText !== '' && Number.isFinite(resultNumber) && Number.isFinite(rlNumber)) {
          if (resultNumber < rlNumber) {
            merged.value = rlText;
            merged.verdict = 'less';
          } else if (resultNumber > rlNumber && merged.verdict === 'less' && changedFields.some(field => ['value','reportingLimit'].includes(field.key))) {
            merged.verdict = '';
          }
        }
        results[parameter] = merged;
      });
      try {
        await db.updateSample(sample.id, {
          ...(sampleChanged ? { results } : {}),
          ...(markWorkOrderComplete ? { completed:true, dateCompleted:todayStr() } : {}),
        });
      } catch (error) {
        failures.push({ id:sample.id, message:error.message || 'Server update failed' });
      }
      setProgress({ done:index + 1, total:targets.length });
    }

    setSaving(false);
    setProgress(null);
    if (failures.length) {
      toast('error', `Saved ${targets.length - failures.length} of ${targets.length} samples. Not saved: ${failures.map(item => item.id).join(', ')}.`);
      return;
    }
    try {
      await saveChangedUnitAndSpecDefaults();
    } catch (error) {
      toast('warn', `Results were saved, but the last-used Unit/Specification defaults were not saved: ${error.message}`);
      return;
    }
    toast('success', markWorkOrderComplete
      ? `Results saved and all ${samples.length} samples in Work Order ${workOrder} marked complete.`
      : `${changedCells.size} field(s) saved for ${targets.length} sample(s) in Work Order ${workOrder}.`);
    onSaved && onSaved();
    onClose();
  };

  const thStyle = { position:'sticky', top:0, zIndex:4, background:'#20386d', color:'white', padding:'7px 8px', borderRight:'1px solid #52658f', whiteSpace:'normal', lineHeight:1.2 };
  const inputStyle = changed => ({ width:'100%', boxSizing:'border-box', border:changed?'2px solid #1b7fd0':'1px solid #cbd5e5', borderRadius:2, padding:'5px 6px', fontSize:11, background:changed?'#eaf5ff':'white', outline:'none' });
  const specificationIncludedCount = samples.filter(sample =>
    sample.includeSpecificationInReport === true
  ).length;

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()} style={{zIndex:1200}}>
      <div className="modal" onClick={event => event.stopPropagation()} style={{width:'96vw',maxWidth:'96vw',height:'88vh',display:'flex',flexDirection:'column'}}>
        <div className="modal-header">
          <div>
            <h2 style={{margin:0}}>📊 Work Order Results Worksheet — {workOrder}</h2>
            <div style={{fontSize:11,opacity:0.8,marginTop:3}}>{samples.length} samples · {parameters.length} available parameters</div>
          </div>
          <button className="modal-close" onClick={onClose} disabled={saving}>✕</button>
        </div>

        <div style={{padding:'9px 12px',background:'#eef5ff',borderBottom:'1px solid #c7d6ea'}}>
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <div style={{display:'inline-flex',border:'1px solid #8ca5c7',borderRadius:4,overflow:'hidden'}}>
              <button type="button" onClick={() => { setGridMode('parameter'); setPasteMessage(''); }} disabled={saving}
                style={{border:0,padding:'6px 10px',fontSize:11,fontWeight:700,cursor:'pointer',background:gridMode==='parameter'?'#20386d':'white',color:gridMode==='parameter'?'white':'#20386d'}}>
                One Parameter · All Samples
              </button>
              <button type="button" onClick={() => { setGridMode('sample'); setShowOrderPanel(false); setPasteMessage(''); }} disabled={saving}
                style={{border:0,borderLeft:'1px solid #8ca5c7',padding:'6px 10px',fontSize:11,fontWeight:700,cursor:'pointer',background:gridMode==='sample'?'#20386d':'white',color:gridMode==='sample'?'white':'#20386d'}}>
                One Sample · All Parameters
              </button>
            </div>
            {gridMode === 'parameter' ? <>
              <label style={{fontWeight:700}}>Parameter</label>
              <select value={selectedParameter} onChange={event => setSelectedParameter(event.target.value)} disabled={saving} style={{minWidth:260,padding:'6px 8px',fontWeight:700}}>
                {parameters.map(parameter => <option key={parameter} value={parameter}>{parameter}</option>)}
              </select>
              <span style={{fontSize:11,color:'#52627a'}}>{assignedSamples.length} of {samples.length} samples assigned</span>
              <button className="btn btn-sm" onClick={() => setShowOrderPanel(value => !value)} disabled={saving}>
                ↕ {showOrderPanel ? 'Hide Parameter Order' : 'Reorder Parameters'}
              </button>
            </> : <>
              <label style={{fontWeight:700}}>Sample</label>
              <select value={selectedSampleId} onChange={event => { setSelectedSampleId(event.target.value); setPasteMessage(''); }} disabled={saving}
                style={{minWidth:260,padding:'6px 8px',fontWeight:700}}>
                {samples.map(sample => <option key={sample.id} value={String(sample.id)}>{sample.id} — {sample.desc || sample.description || 'No description'}</option>)}
              </select>
              <span style={{fontSize:11,color:'#52627a'}}>{selectedSampleParameters.length} parameters assigned</span>
            </>}
            <label style={{display:'inline-flex',alignItems:'center',gap:5,padding:'5px 7px',background:'white',border:'1px solid #b8c7de',borderRadius:4,fontSize:11,fontWeight:700}}>
              <input type="checkbox" checked={includeSpecificationForWorkOrder}
                onChange={event => setIncludeSpecificationForWorkOrder(event.target.checked)}
                disabled={saving} />
              Include Specification column for all samples
            </label>
            <button className="btn btn-sm" onClick={saveWorkOrderSpecificationSetting}
              disabled={saving || changedCells.size > 0}
              title={changedCells.size > 0 ? 'Save changed result cells first' : 'Apply this report-column setting to every sample in the work order'}>
              {saving ? '⏳ Applying…' : 'Apply to Work Order'}
            </button>
            <span style={{fontSize:10,color:'#52627a'}}>
              Current: {specificationIncludedCount}/{samples.length} included
            </span>
            <button className="btn btn-sm" type="button" onClick={() => setShowBulkPaste(value => !value)} disabled={saving} style={{background:'#dff5e8',fontWeight:700}}>
              {showBulkPaste ? 'Hide Bulk Excel Paste' : '📋 Bulk Excel Paste'}
            </button>
            {pasteMessage && <span style={{fontSize:11,color:'#176b3a',fontWeight:700}}>{pasteMessage}</span>}
          </div>
          <div style={{marginTop:7,fontSize:11,color:'#294766'}}>
            {gridMode === 'parameter'
              ? <>Copy an Excel column, click the first <strong>Result</strong> cell, and paste. Rows follow Sample ID order. You can also paste a table containing a <strong>Sample ID</strong> column and recognized field headers.</>
              : <>Copy the Excel results column, click the first <strong>Result</strong> cell, and paste. Rows follow this sample's parameter order, so one sample with 20 parameters accepts 20 pasted results at once.</>}
          </div>
        </div>

        {showBulkPaste && (
          <div style={{padding:12,background:'#f1f8ff',borderBottom:'2px solid #abc8e5',maxHeight:'46vh',overflowY:'auto'}}>
            <strong>Bulk Excel Paste — map columns without changing parameter order</strong>
            <div style={{fontSize:12,margin:'6px 0'}}>Copy only result values from Excel (no Sample ID or header). Paste rows in the same order as the Work Grid Sample IDs. Blank cells are skipped.</div>
            <label style={{fontSize:12,fontWeight:700}}>First target sample: </label>
            <select value={bulkPasteStart} onChange={event => setBulkPasteStart(event.target.value)} disabled={saving} style={{padding:5,marginBottom:8}}>
              {samples.map((sample, index) => <option key={sample.id} value={String(index)}>{sample.id} (row {index + 1})</option>)}
            </select>
            <textarea aria-label="Paste Excel result values" value={bulkPasteText} onChange={event => {setBulkPasteText(event.target.value);setBulkPasteMappings([]);}} placeholder="Paste Excel cells here (Ctrl+V)" rows={4} style={{display:'block',width:'100%',boxSizing:'border-box',fontFamily:'monospace',fontSize:12,border:'1px solid #a9bdd8',padding:8}} />
            {bulkPasteWidth > 0 && <>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',margin:'9px 0'}}>
                {bulkPasteMap.map((mapping, index) => (
                  <label key={index} style={{display:'flex',flexDirection:'column',fontSize:11,fontWeight:700,minWidth:160,maxWidth:260,flex:'1 1 180px'}}>
                    Excel column {index + 1}
                    <select aria-label={`Map Excel column ${index + 1}`} value={mapping} onChange={event => setBulkPasteMappings(old => {const next=[...old];next[index]=event.target.value;return next;})} style={{width:'100%',padding:5}}>
                      <option value="">-- Select parameter --</option>
                      {parameters.map(parameter => <option key={parameter} value={parameter} disabled={bulkPasteMap.includes(parameter) && mapping !== parameter}>{parameter}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <div style={{fontSize:12,fontWeight:700,margin:'8px 0'}}>Preview: {bulkPasteRows.length} rows × {bulkPasteWidth} columns; {bulkPasteUpdates.length} results ready; {bulkPasteSkipped} blank cells skipped.</div>
              <div style={{overflowX:'auto',maxHeight:160}}>
                <table style={{borderCollapse:'collapse',fontSize:11,minWidth:'100%'}}>
                  <thead><tr><th style={{padding:5,background:'#d7e8f9'}}>Existing Sample ID</th>{bulkPasteMap.map((parameter,i) => <th key={i} style={{padding:5,background:'#d7e8f9'}}>{parameter || `Column ${i+1}: not mapped`}</th>)}</tr></thead>
                  <tbody>{bulkPasteRows.slice(0,6).map((row,i) => <tr key={i}><td style={{padding:5,border:'1px solid #d4deeb'}}>{samples[bulkPasteStartIndex+i]?.id || 'No sample'}</td>{bulkPasteMap.map((_,j) => <td key={j} style={{padding:5,border:'1px solid #d4deeb'}}>{row[j] ?? ''}</td>)}</tr>)}</tbody>
                </table>
              </div>
              {bulkPasteErrors.length > 0 && <div role="alert" style={{color:'#a32626',fontSize:12,marginTop:8}}>{[...new Set(bulkPasteErrors)].slice(0,8).map((error,i)=><div key={i}>⚠ {error}</div>)}</div>}
              {bulkPasteReplaced > 0 && <div style={{fontSize:12,color:'#9c5d0a',marginTop:8}}>Warning: {bulkPasteReplaced} existing/draft result(s) will be replaced; confirmation required.</div>}
              <div style={{display:'flex',gap:8,marginTop:10}}>
                <button type="button" className="btn btn-success" disabled={!bulkPasteUpdates.length || !!bulkPasteErrors.length || saving} onClick={applyMappedPaste}>Apply to Work Grid (not yet saved)</button>
                <button type="button" className="btn btn-sm" onClick={() => setShowBulkPaste(false)}>Cancel</button>
              </div>
            </>}
          </div>
        )}

        {gridMode === 'parameter' && showOrderPanel && (
          <div style={{padding:'10px 12px',borderBottom:'1px solid #c7d6ea',background:'#f4fbf6',display:'flex',gap:12,alignItems:'stretch',flexWrap:'wrap'}}>
            <div style={{minWidth:340,flex:'1 1 420px'}}>
              <div style={{fontSize:12,fontWeight:700,color:'#215a36',marginBottom:5}}>Work Order Parameter Order</div>
              <select size={Math.min(10, Math.max(4, parameterOrder.length))} value={orderSelection}
                onChange={event => setOrderSelection(event.target.value)} disabled={saving}
                style={{width:'100%',minHeight:110,padding:5,fontSize:11,border:'1px solid #9fc8aa'}}>
                {parameterOrder.map((parameter, index) => (
                  <option key={parameter} value={parameter}>{index + 1}. {parameter}</option>
                ))}
              </select>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:5,justifyContent:'center',minWidth:130}}>
              <button className="btn btn-sm" onClick={() => moveOrderSelection('top')} disabled={saving || parameterOrder.indexOf(orderSelection) <= 0}>⇈ Move to Top</button>
              <button className="btn btn-sm" onClick={() => moveOrderSelection(-1)} disabled={saving || parameterOrder.indexOf(orderSelection) <= 0}>↑ Move Up</button>
              <button className="btn btn-sm" onClick={() => moveOrderSelection(1)} disabled={saving || parameterOrder.indexOf(orderSelection) < 0 || parameterOrder.indexOf(orderSelection) >= parameterOrder.length - 1}>↓ Move Down</button>
              <button className="btn btn-sm" onClick={() => moveOrderSelection('bottom')} disabled={saving || parameterOrder.indexOf(orderSelection) < 0 || parameterOrder.indexOf(orderSelection) >= parameterOrder.length - 1}>⇊ Move to Bottom</button>
            </div>
            <div style={{flex:'1 1 300px',display:'flex',flexDirection:'column',justifyContent:'center',gap:8}}>
              <div style={{fontSize:11,color:'#365b43',lineHeight:1.45}}>
                Applies one shared ranking to every sample in this work order. Each sample keeps only its assigned parameters. Result records are not included in the save request.
              </div>
              {changedCells.size > 0 && <div style={{fontSize:11,color:'#a15c00',fontWeight:700}}>Save the changed result cells first, then reopen the grid to apply the order.</div>}
              <button className="btn btn-success" onClick={saveParameterOrder}
                disabled={!orderChanged || saving || changedCells.size > 0}>
                {saving ? '⏳ Saving Order…' : `✅ Apply Order to Work Order (${samples.length} samples)`}
              </button>
            </div>
          </div>
        )}

        {gridMode === 'parameter' && <div style={{padding:'8px 12px',borderBottom:'1px solid #d6deeb',background:'#fbfcff',display:'grid',gridTemplateColumns:'repeat(6,minmax(145px,1fr))',gap:7}}>
          {BULK_FIELDS.map(field => <div key={field.key} style={{border:'1px solid #ccd7e8',borderRadius:4,padding:6,background:'white'}}>
            <div style={{fontSize:10,fontWeight:700,marginBottom:4,color:'#20386d'}}>{field.label}</div>
            <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
              <input type={field.key === 'dateTested' ? 'date' : 'text'} value={bulkValues[field.key] ?? ''} onChange={event => setBulkValues(previous => ({...previous,[field.key]:event.target.value}))} disabled={saving} style={{minWidth:0,flex:'1 1 80px',padding:'4px 5px',fontSize:11}} />
              <button className="btn btn-sm" onClick={() => applyBulk(field.key, true)} disabled={!selectedParameter || !selectedRows.size || saving} title={`Apply ${field.label} only to checked rows`}>↓ Selected</button>
              <button className="btn btn-sm" onClick={() => applyBulk(field.key, false)} disabled={!selectedParameter || !assignedSamples.length || saving} title={`Apply ${field.label} to every sample assigned ${selectedParameter}`}>↓ All</button>
            </div>
          </div>)}
          <div style={{border:'1px solid #d2b464',borderRadius:4,padding:6,background:'#fff9e8'}}>
            <div style={{fontSize:10,fontWeight:700,marginBottom:4,color:'#765400'}}>Result Tools</div>
            <div style={{display:'flex',gap:4,alignItems:'center',flexWrap:'wrap'}}>
              <select value={roundDigits} onChange={event => setRoundDigits(event.target.value)} disabled={saving} style={{padding:'4px 5px',fontSize:11}}>
                {[0,1,2,3,4,5,6].map(value => <option key={value} value={value}>{value} decimal{value === 1 ? '' : 's'}</option>)}
              </select>
              <button className="btn btn-sm" onClick={roundSelectedResults} disabled={!selectedRows.size || saving}>Round Selected</button>
              <button className="btn btn-sm" onClick={() => applyLessThanRule(true)} disabled={!selectedRows.size || saving} title="Apply the Less Than RL rule to checked rows">&lt; RL Selected</button>
              <button className="btn btn-sm" onClick={() => applyLessThanRule(false)} disabled={!assignedSamples.length || saving} title="Apply the Less Than RL rule to every assigned row">&lt; RL All</button>
              <button className="btn btn-sm" onClick={() => setSelectedRows(new Set())} disabled={!selectedRows.size || saving}>Clear</button>
            </div>
          </div>
        </div>}

        <div style={{flex:1,overflow:'auto',background:'white'}}>
          {parameters.length === 0 ? <div className="notice notice-warn" style={{margin:18}}>No parameters are assigned to samples in this Work Order.</div> : gridMode === 'parameter' ? (
            <table style={{borderCollapse:'separate',borderSpacing:0,minWidth:1320,width:'100%',fontSize:11}}>
              <thead><tr>
                <th style={{...thStyle,left:0,zIndex:7,minWidth:38,width:38,textAlign:'center'}} title="Select all samples assigned this parameter">
                  <input type="checkbox" checked={assignedSamples.length > 0 && assignedSamples.every(sample => selectedRows.has(String(sample.id)))} onChange={toggleAllAssigned} disabled={!assignedSamples.length || saving} />
                </th>
                <th style={{...thStyle,left:38,zIndex:6,minWidth:95}}>Sample ID</th>
                <th style={{...thStyle,left:133,zIndex:6,minWidth:210}}>Description</th>
                {FIELDS.map(field => <th key={field.key} style={{...thStyle,minWidth:field.key==='method'||field.key==='spec'?165:field.key==='dateTested'?125:90}}>{field.label}</th>)}
              </tr></thead>
              <tbody>{samples.map((sample, rowIndex) => {
                const sampleId = String(sample.id);
                const assigned = (sample.params || []).includes(selectedParameter);
                const draft = drafts[sampleId]?.[selectedParameter] || defaultDraft(selectedParameter, sample.results?.[selectedParameter], sample);
                return <tr key={sampleId} style={{opacity:assigned?1:0.55}}>
                  <td style={{position:'sticky',left:0,zIndex:3,background:selectedRows.has(sampleId)?'#fff4c7':'#f3f6fc',padding:'5px 8px',borderRight:'1px solid #cbd5e5',borderBottom:'1px solid #dde4ef',textAlign:'center'}}>
                    <input type="checkbox" checked={selectedRows.has(sampleId)} onChange={() => toggleRow(sampleId)} disabled={!assigned || saving} />
                  </td>
                  <td style={{position:'sticky',left:38,zIndex:2,background:selectedRows.has(sampleId)?'#fff4c7':'#f3f6fc',padding:'5px 8px',borderRight:'1px solid #cbd5e5',borderBottom:'1px solid #dde4ef',fontWeight:700,fontFamily:'monospace'}}>{sampleId}</td>
                  <td title={sample.desc || sample.description || ''} style={{position:'sticky',left:133,zIndex:2,background:selectedRows.has(sampleId)?'#fff9df':'#f8faff',padding:'5px 8px',borderRight:'1px solid #cbd5e5',borderBottom:'1px solid #dde4ef',maxWidth:230,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{sample.desc || sample.description || '—'}</td>
                  {FIELDS.map((field, fieldIndex) => {
                    const changed = changedCells.has(cellKey(sampleId, selectedParameter, field.key));
                    return <td key={field.key} style={{padding:2,borderRight:'1px solid #dde4ef',borderBottom:'1px solid #dde4ef',background:assigned?'white':'#f2f2f2'}}>
                      {field.key === 'verdict' ? (
                        <select value={draft.verdict || ''} onChange={event => changeCell(sampleId, 'verdict', event.target.value)} disabled={!assigned || saving} title={`${sampleId} — ${selectedParameter} — Verdict`} style={{...inputStyle(changed),fontWeight:draft.verdict?700:400,color:draft.verdict==='less'?'#b83232':'inherit',background:assigned?(changed?'#eaf5ff':'white'):'#f2f2f2'}}>
                          {VERDICT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      ) : (
                        <input type={field.key === 'dateTested' ? 'date' : 'text'} value={draft[field.key] ?? ''} onChange={event => changeCell(sampleId, field.key, event.target.value)} onBlur={field.key === 'value' ? () => normalizeResultCell(sampleId) : field.key === 'reportingLimit' ? event => normalizeResultCell(sampleId, event.target.value) : undefined} onPaste={event => pasteAt(event, rowIndex, fieldIndex)} disabled={!assigned || saving} title={assigned ? `${sampleId} — ${selectedParameter} — ${field.label}` : `${selectedParameter} is not assigned to this sample`} style={{...inputStyle(changed),textAlign:['value','reportingLimit','dilutionFactor'].includes(field.key)?'right':'left',fontFamily:['value','reportingLimit','dilutionFactor'].includes(field.key)?'monospace':'inherit',fontWeight:field.key==='value'?700:400,background:assigned?(changed?'#eaf5ff':'white'):'#f2f2f2'}} />
                      )}
                    </td>;
                  })}
                </tr>;
              })}</tbody>
            </table>
          ) : !selectedSample ? <div className="notice notice-warn" style={{margin:18}}>No sample is available in this Work Order.</div> : (
            <table style={{borderCollapse:'separate',borderSpacing:0,minWidth:1120,width:'100%',fontSize:11}}>
              <thead><tr>
                <th style={{...thStyle,left:0,zIndex:6,minWidth:250}}>Parameter — Sample {selectedSample.id}</th>
                {FIELDS.map(field => <th key={field.key} style={{...thStyle,minWidth:field.key==='method'||field.key==='spec'?165:field.key==='dateTested'?125:90}}>{field.label}</th>)}
              </tr></thead>
              <tbody>{selectedSampleParameters.map((parameter, parameterIndex) => {
                const draft = drafts[selectedSampleId]?.[parameter] || defaultDraft(parameter, selectedSample.results?.[parameter], selectedSample);
                return <tr key={parameter}>
                  <td title={parameter} style={{position:'sticky',left:0,zIndex:2,background:'#f3f6fc',padding:'5px 8px',borderRight:'1px solid #cbd5e5',borderBottom:'1px solid #dde4ef',fontWeight:700,maxWidth:280,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                    {parameterIndex + 1}. {parameter}
                  </td>
                  {FIELDS.map((field, fieldIndex) => {
                    const changed = changedCells.has(cellKey(selectedSampleId, parameter, field.key));
                    return <td key={field.key} style={{padding:2,borderRight:'1px solid #dde4ef',borderBottom:'1px solid #dde4ef',background:'white'}}>
                      {field.key === 'verdict' ? (
                        <select value={draft.verdict || ''} onChange={event => changeSampleParameterCell(parameter, 'verdict', event.target.value)} disabled={saving}
                          title={`${selectedSample.id} — ${parameter} — Verdict`}
                          style={{...inputStyle(changed),fontWeight:draft.verdict?700:400,color:draft.verdict==='less'?'#b83232':'inherit'}}>
                          {VERDICT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      ) : (
                        <input type={field.key === 'dateTested' ? 'date' : 'text'} value={draft[field.key] ?? ''}
                          onChange={event => changeSampleParameterCell(parameter, field.key, event.target.value)}
                          onBlur={field.key === 'value'
                            ? () => normalizeSingleSampleResultCell(parameter)
                            : field.key === 'reportingLimit'
                              ? event => normalizeSingleSampleResultCell(parameter, event.target.value)
                              : undefined}
                          onPaste={event => pasteSingleSampleAt(event, parameterIndex, fieldIndex)} disabled={saving}
                          title={`${selectedSample.id} — ${parameter} — ${field.label}`}
                          style={{...inputStyle(changed),textAlign:['value','reportingLimit','dilutionFactor'].includes(field.key)?'right':'left',fontFamily:['value','reportingLimit','dilutionFactor'].includes(field.key)?'monospace':'inherit',fontWeight:field.key==='value'?700:400}} />
                      )}
                    </td>;
                  })}
                </tr>;
              })}</tbody>
            </table>
          )}
        </div>

        <div className="modal-footer" style={{justifyContent:'space-between'}}>
          <div style={{fontSize:11,color:'#52627a'}}>
            {progress ? `Saving sample ${progress.done} of ${progress.total}…` : `${selectedRows.size} selected · ${changedCells.size} changed field(s) across ${changedSampleIds.length} sample(s)`}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary" onClick={() => saveAll(false)} disabled={!changedCells.size || saving}>{saving ? '⏳ Saving…' : '💾 Save All Changed Results'}</button>
            <button className="btn btn-success" onClick={() => saveAll(true)} disabled={saving || (!changedCells.size && samples.every(sample => sample.completed))} title="Save changes and make every sample in this Work Order visible in Completed Reports">✅ Save & Complete Work Order</button>
            <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
